import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Lock, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";

function PeachWidget({ widgetUrl, integrity }) {
  const host = useRef(null);
  useEffect(() => {
    window.wpwlOptions = { style: "card", locale: "en", showCVVHint: true, brandDetection: true };
    const form = document.createElement("form");
    form.action = `${window.location.origin}/payment/result`;
    form.className = "paymentWidgets";
    form.dataset.brands = "VISA MASTER AMEX";
    const script = document.createElement("script");
    script.src = widgetUrl;
    if (integrity) {
      script.integrity = integrity;
      script.crossOrigin = "anonymous";
    }
    host.current?.append(form, script);
    return () => {
      form.remove();
      script.remove();
    };
  }, [widgetUrl, integrity]);
  return <div ref={host} className="peach-widget" data-testid="peach-widget" />;
}

export default function Checkout() {
  const { state } = useLocation();
  const navigate = useNavigate();

  if (!state?.widget_url) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="checkout-missing">
        <div className="text-center">
          <p className="text-zinc-400 mb-4">This checkout session has expired.</p>
          <Button onClick={() => navigate("/pricing")} className="bg-orange-600 hover:bg-orange-500">Back to pricing</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4 md:px-8" data-testid="checkout-page">
      <SEO url="/checkout" title="Secure Checkout" description="Pay securely with Peach Payments." />
      <div className="max-w-lg mx-auto">
        <h1 className="font-heading text-4xl uppercase mb-2">
          SECURE <span className="text-orange-500">CHECKOUT</span>
        </h1>
        <div className="bg-zinc-900 border border-zinc-800 p-5 mb-6 flex items-center justify-between gap-4" data-testid="checkout-summary">
          <div>
            <p className="text-white font-medium">{state.description}</p>
            {state.mode === "test" && <p className="text-xs text-yellow-400 mt-1">Test mode — no real money is charged</p>}
          </div>
          <p className="font-heading text-2xl text-orange-500" data-testid="checkout-amount">R{Number(state.amount).toFixed(2)}</p>
        </div>
        <div className="bg-zinc-100 p-4 sm:p-6">
          <PeachWidget widgetUrl={state.widget_url} integrity={state.integrity} />
        </div>
        <p className="text-xs text-zinc-500 mt-4 flex items-center gap-2">
          <Lock className="h-3 w-3" /> Card details are entered on Peach Payments' secure form and never touch our servers.
        </p>
        {state.description?.includes("monthly") && (
          <p className="text-xs text-zinc-500 mt-2 flex items-center gap-2">
            <ShieldCheck className="h-3 w-3" /> Your card will be saved securely and charged monthly until you cancel from your dashboard.
          </p>
        )}
      </div>
    </div>
  );
}
